import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  Text,
  Divider,
  ColorsV2,
  useThemeColors,
  Flex,
  Box,
  Tooltip,
  Icon,
  Pane,
  spaces,
} from '@electricjs/arc';

import { ExternalLink, InlineLazyText } from '@common';

import useDebounceValue from '@/hooks/useDebounceValue';
import { getLocalizedSupportUrl } from '@/helpers/getLocalizedSupportUrl';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  useCalculateCatalogShippingCostMutation,
  useCalculateWarehouseShippingCostMutation,
} from '@/redux/slices/orderApiSlice';
import {
  PaymentType,
  useEstimateTaxesMutation,
} from '@/redux/slices/billingApiSlice';
import { useGlobalUI } from '@/components/GlobalUIProvider';

import { emptyValue } from '@/constants/common';
import formatCurrency from '@/helpers/formatCurrency';
import { StorefrontSteps } from '@/types/storefront';
import { EstimatedTaxes } from '@/types/billing';

import { useStorefront } from '../../StorefrontProvider';
import {
  calculatePreTaxMinMonthlyInstallment,
  calculateOrderTotals,
  isWarehouseAsset,
} from '../../utils/helpers';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

const StyledFlex = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: 0.4rem;
`;

const OrderSummary = () => {
  const { t } = useTranslation('storefront');
  const [dividerColor, legalTextColor] = useThemeColors([
    ColorsV2.GRAY_LIGHT,
    ColorsV2.GRAY_DARK,
  ]);

  const { showErrorToast } = useGlobalUI();

  const {
    storefrontCheckoutForm,
    catalogCart,
    assetCart,
    itemsInCart,
    catalogShippingCost,
    setCatalogShippingCost,
    warehouseShippingCost,
    setWarehouseShippingCost,
    cartRequiresCatalogShippingMethod,
    cartRequiresWarehouseShippingMethod,
    cartRequiresTax,
    subTotalPrice,
    orderTotal,
    setOrderTotal,
    todayTotal,
    setTodayTotal,
    monthlyInstallment,
    setMonthlyInstallment,
    hasOnlyAssetsInCart,
    storefrontStep,
    orderCurrency,
  } = useStorefront();

  const warehouseAssets = useMemo(
    () => assetCart.filter(isWarehouseAsset),
    [assetCart]
  );

  const [estimatedTaxes, setEstimatedTaxes] = useState<
    EstimatedTaxes | undefined
  >();
  const { storefrontPayOverTime } = useFlags();
  const hasInstallmentsEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Installments
  );
  const showFinancing = storefrontPayOverTime && hasInstallmentsEntitlement;

  const shoppingCountry = storefrontCheckoutForm?.watch('shoppingCountry');

  const countryCode =
    shoppingCountry?.value && shoppingCountry.value !== 'other'
      ? shoppingCountry.value
      : undefined;

  const selectedCatalogShippingMethod = storefrontCheckoutForm?.watch(
    'catalogShippingMethod'
  )?.value;

  const selectedWarehouseShippingMethod = storefrontCheckoutForm?.watch(
    'warehouseShippingMethod'
  )?.value;

  const shippingAddress = storefrontCheckoutForm?.watch('shippingAddress');

  const selectedPaymentType = storefrontCheckoutForm?.watch('paymentType');

  const selectedInstallmentsCount = storefrontCheckoutForm?.watch(
    'paymentInstallmentsCount'
  );

  const organizationId = useGetOrganizationId();

  const [
    calculateCatalogShippingCost,
    {
      isLoading: catalogShippingCostLoading,
      isError: catalogShippingCostError,
    },
  ] = useCalculateCatalogShippingCostMutation();

  const [
    calculateWarehouseShippingCost,
    {
      isLoading: warehouseShippingCostLoading,
      isError: warehouseShippingCostError,
    },
  ] = useCalculateWarehouseShippingCostMutation();

  const [estimateTaxes, { isLoading: taxesLoading, isError: taxesError }] =
    useEstimateTaxesMutation();

  // calculate the catalogShippingCost every time country or catalog shipping method or catalog cart change
  useEffect(() => {
    if (
      countryCode &&
      selectedCatalogShippingMethod &&
      cartRequiresCatalogShippingMethod
    ) {
      calculateCatalogShippingCost({
        organizationId,
        countryCode,
        selectedCatalogShippingMethod,
        selectedProducts: catalogCart,
      })
        .unwrap()
        .then(value => setCatalogShippingCost(value))
        .catch(() => {
          showErrorToast({
            id: 'shipping-cost-error-toast',
            title: t('sections.orderSummary.errors.shippingCalculationTitle'),
            message: t(
              'sections.orderSummary.errors.shippingCalculationMessage'
            ),
            hideAfter: 5000,
          });

          // resetting the catalog shipping method, so when the user re-selects it, the calculation will be triggered again
          storefrontCheckoutForm.setValue('catalogShippingMethod', undefined);
          setCatalogShippingCost(undefined);
        });
    }

    // handle when catalog cart gets empty or shipping method is reset
    if (!cartRequiresCatalogShippingMethod || !selectedCatalogShippingMethod) {
      setCatalogShippingCost(undefined);
    }
  }, [
    organizationId,
    countryCode,
    selectedCatalogShippingMethod,
    catalogCart,
    cartRequiresCatalogShippingMethod,
    calculateCatalogShippingCost,
    setCatalogShippingCost,
    showErrorToast,
    storefrontCheckoutForm,
    t,
  ]);

  // calculate the warehouseShippingCost every time warehouse shipping method or warehouse assets change
  useEffect(() => {
    if (
      cartRequiresWarehouseShippingMethod &&
      selectedWarehouseShippingMethod
    ) {
      calculateWarehouseShippingCost({
        organizationId,
        selectedWarehouseShippingMethod,
        warehouseAssetIds: warehouseAssets.map(entry => entry.item.id),
      })
        .unwrap()
        .then(value => setWarehouseShippingCost(value))
        .catch(() => {
          showErrorToast({
            id: 'warehouse-shipping-cost-error-toast',
            title: t(
              'sections.orderSummary.errors.warehouseShippingCalculationTitle'
            ),
            message: t(
              'sections.orderSummary.errors.warehouseShippingCalculationMessage'
            ),
            hideAfter: 5000,
          });

          storefrontCheckoutForm.setValue('warehouseShippingMethod', undefined);
          setWarehouseShippingCost(undefined);
        });
    }

    // handle when asset cart gets empty (at least empty of warehouse assets) or shipping method is reset
    if (
      !cartRequiresWarehouseShippingMethod ||
      !selectedWarehouseShippingMethod
    ) {
      setWarehouseShippingCost(undefined);
    }
  }, [
    organizationId,
    selectedWarehouseShippingMethod,
    warehouseAssets,
    cartRequiresWarehouseShippingMethod,
    calculateWarehouseShippingCost,
    setWarehouseShippingCost,
    showErrorToast,
    storefrontCheckoutForm,
    t,
  ]);

  const debouncedShippingAddress = useDebounceValue(shippingAddress, 1000);

  // estimate the taxes every time address, catalogShippingCost, warehouseShippingCost, or cart change
  useEffect(() => {
    const addressIsValid =
      debouncedShippingAddress !== undefined &&
      debouncedShippingAddress.streetAddress1 && // check if the address is full (if Google api returned all the properties needed)
      debouncedShippingAddress.city &&
      debouncedShippingAddress.state &&
      debouncedShippingAddress.country &&
      debouncedShippingAddress.zip;

    // All required shipping costs must be resolved before estimating taxes, so the shipping methods must be selected and costs must have value
    const catalogShippingReady =
      !cartRequiresCatalogShippingMethod ||
      (selectedCatalogShippingMethod !== undefined &&
        catalogShippingCost !== undefined);

    const warehouseShippingReady =
      !cartRequiresWarehouseShippingMethod ||
      (selectedWarehouseShippingMethod !== undefined &&
        warehouseShippingCost !== undefined);

    if (
      addressIsValid &&
      cartRequiresTax &&
      catalogShippingReady &&
      warehouseShippingReady
    ) {
      estimateTaxes({
        organizationId,
        shippingAddress: debouncedShippingAddress,
        currency: orderCurrency,
        catalogShippingCost: catalogShippingCost ?? 0,
        warehouseShippingCost,
        warehouseShippingMethod: selectedWarehouseShippingMethod,
        selectedProducts: catalogCart,
        selectedPaymentType:
          catalogCart.length > 0 && selectedPaymentType
            ? selectedPaymentType
            : PaymentType.InFull,
        selectedInstallmentsCount:
          catalogCart.length > 0 ? selectedInstallmentsCount : undefined,
      })
        .unwrap()
        .then(value => setEstimatedTaxes(value))
        .catch(() => {
          showErrorToast({
            id: 'tax-error-toast',
            title: t('sections.orderSummary.errors.taxEstimationTitle'),
            message: t('sections.orderSummary.errors.taxEstimationMessage'),
            hideAfter: 5000,
          });

          // resetting the shipping address, so when the user re-enters it, the calculation will be triggered again
          storefrontCheckoutForm?.setValue('shippingAddress', undefined);
        });
    }

    // handle shipping address clearing or cart becoming empty
    if (
      debouncedShippingAddress === undefined ||
      debouncedShippingAddress.zip === '' ||
      !cartRequiresTax
    ) {
      setEstimatedTaxes(undefined);
    }
  }, [
    organizationId,
    orderCurrency,
    catalogCart,
    cartRequiresCatalogShippingMethod,
    selectedCatalogShippingMethod,
    cartRequiresWarehouseShippingMethod,
    selectedWarehouseShippingMethod,
    cartRequiresTax,
    showErrorToast,
    storefrontCheckoutForm,
    catalogShippingCost,
    warehouseShippingCost,
    debouncedShippingAddress,
    selectedPaymentType,
    selectedInstallmentsCount,
    estimateTaxes,
    t,
  ]);

  // Calculate and keep track of "Order's total", "Today's total" and "Monthly installment"
  useEffect(() => {
    const result = calculateOrderTotals({
      paymentType: selectedPaymentType ?? PaymentType.InFull,
      installmentsCount: selectedInstallmentsCount,
      orderSubTotal: subTotalPrice,
      catalogShippingCost: catalogShippingCost ?? 0,
      warehouseShippingCost: warehouseShippingCost ?? 0,
      totalTaxes: estimatedTaxes?.totalTaxes ?? 0,
      todayTax: estimatedTaxes?.todayTax ?? 0,
      futureMonthlyTax: estimatedTaxes?.futureMonthlyTax,
    });

    // saving them in the Context (so they can be used on Confirmation step)
    if (result) {
      setOrderTotal(result.orderTotal);
      setTodayTotal(result.todayTotal);
      setMonthlyInstallment(result.monthlyInstallment);
    }
  }, [
    setOrderTotal,
    setTodayTotal,
    setMonthlyInstallment,
    selectedPaymentType,
    subTotalPrice,
    catalogShippingCost,
    warehouseShippingCost,
    estimatedTaxes,
    selectedInstallmentsCount,
  ]);

  const formatValue = (value: number | undefined) => {
    if (value === undefined) {
      return emptyValue;
    }

    return formatCurrency(value, orderCurrency);
  };

  return (
    <Pane stack elevated padding={spaces.space_l}>
      <Text variant="heading-1">{t('sections.orderSummary.title')}</Text>
      <Box minWidth="15rem" mt={spaces.space_m_rem}>
        <StyledFlex>
          {/* Subtotal (sum of the prices of all items in the cart, without catalog shipping cost or tax) */}
          <Text>
            {t('sections.orderSummary.subtotal')} ({itemsInCart}):
          </Text>
          <Text>{formatCurrency(subTotalPrice, orderCurrency)}</Text>
        </StyledFlex>

        {/* Warehouse shipping */}
        {cartRequiresWarehouseShippingMethod && (
          <Box mt="8px">
            <StyledFlex>
              <Text>{t('sections.orderSummary.warehouseShipping')}:</Text>
              <InlineLazyText
                loading={warehouseShippingCostLoading}
                error={warehouseShippingCostError}>
                {formatValue(warehouseShippingCost)}
              </InlineLazyText>
            </StyledFlex>
            {warehouseShippingCost === undefined && (
              <Text variant="legal">
                {t('sections.orderSummary.warehouseShippingCalculated')}
              </Text>
            )}
          </Box>
        )}

        {/* Catalog shipping */}
        {cartRequiresCatalogShippingMethod && (
          <Box mt="8px">
            <StyledFlex>
              <Text>
                {cartRequiresWarehouseShippingMethod
                  ? t('sections.orderSummary.purchasesShipping')
                  : t('sections.orderSummary.shipping')}
                :
              </Text>
              <InlineLazyText
                loading={catalogShippingCostLoading}
                error={catalogShippingCostError}>
                {formatValue(catalogShippingCost)}
              </InlineLazyText>
            </StyledFlex>
            {catalogShippingCost === undefined && (
              <Text variant="legal">
                {t('sections.orderSummary.shippingCalculated')}
              </Text>
            )}
          </Box>
        )}

        {/* Total taxes */}
        {cartRequiresTax && (
          <Box mt="8px">
            <StyledFlex>
              <Text>{t('sections.orderSummary.totalTaxes')}:</Text>
              <InlineLazyText loading={taxesLoading} error={taxesError}>
                {formatValue(estimatedTaxes?.totalTaxes)}
              </InlineLazyText>
            </StyledFlex>
            {estimatedTaxes === undefined && (
              <Text variant="legal">
                {t('sections.orderSummary.taxesCalculated')}
              </Text>
            )}
          </Box>
        )}

        {/* Financing summary */}
        {!hasOnlyAssetsInCart &&
          selectedPaymentType === PaymentType.InInstallments && (
            <>
              <Divider space="24px" color={dividerColor} />
              <Box width="100%" minWidth="15rem">
                {/* Order financing (monthly installment) */}
                <StyledFlex>
                  <Text>
                    {t('sections.orderSummary.orderFinancing')}:{' '}
                    <Tooltip
                      maxWidth="40rem"
                      content={t('sections.orderSummary.orderFinancingTooltip')}
                      placement="top-start">
                      <Icon
                        icon="information-circle"
                        intent="neutral"
                        size="small"
                      />
                    </Tooltip>
                  </Text>
                  <InlineLazyText
                    loading={taxesLoading}
                    error={taxesError}
                    textProps={{ textAlign: 'right' }}>
                    {`${formatCurrency(monthlyInstallment, orderCurrency)}${t('sections.orderSummary.perMonth')}`}
                  </InlineLazyText>
                </StyledFlex>
                {/* Total financed */}
                <StyledFlex mt="8px">
                  <Text>
                    {t('sections.orderSummary.totalFinanced')}:{' '}
                    <Tooltip
                      maxWidth="40rem"
                      content={t('sections.orderSummary.totalFinancedTooltip')}
                      placement="top-start">
                      <Icon
                        icon="information-circle"
                        intent="neutral"
                        size="small"
                      />
                    </Tooltip>
                  </Text>
                  <InlineLazyText loading={taxesLoading} error={taxesError}>
                    {formatCurrency(
                      subTotalPrice + (estimatedTaxes?.totalTaxes ?? 0),
                      orderCurrency
                    )}
                  </InlineLazyText>
                </StyledFlex>
                {/* Order total */}
                <StyledFlex mt="8px">
                  <Text>
                    {t('sections.orderSummary.orderTotal')}:{' '}
                    <Tooltip
                      maxWidth="40rem"
                      content={t('sections.orderSummary.orderTotalTooltip')}
                      placement="top-start">
                      <Icon
                        icon="information-circle"
                        intent="neutral"
                        size="small"
                      />
                    </Tooltip>
                  </Text>
                  <InlineLazyText loading={taxesLoading} error={taxesError}>
                    {formatCurrency(orderTotal, orderCurrency)}
                  </InlineLazyText>
                </StyledFlex>
              </Box>
            </>
          )}

        {/* Today's total */}
        <Divider space="24px" color={dividerColor} />
        <StyledFlex>
          <Flex vertical>
            <Text variant="heading-3">
              {t('sections.orderSummary.todayTotal')}:
              {!hasOnlyAssetsInCart &&
                selectedPaymentType === PaymentType.InInstallments && (
                  <>
                    {' '}
                    <Tooltip
                      maxWidth="40rem"
                      content={() => (
                        <Flex vertical>
                          <Text>
                            {t('sections.orderSummary.todayTotalTooltip')}
                          </Text>
                          <Text mt="16px">
                            {t(
                              'sections.orderSummary.todayTotalTooltipWarehouseShipping'
                            )}
                          </Text>
                        </Flex>
                      )}
                      placement="top-start">
                      <Icon
                        icon="information-circle"
                        intent="neutral"
                        size="small"
                      />
                    </Tooltip>
                  </>
                )}
            </Text>
            {!hasOnlyAssetsInCart &&
              selectedPaymentType === PaymentType.InInstallments && (
                <Text variant="legal" color={legalTextColor}>
                  {t('sections.orderSummary.includesShippingAndTax')}
                </Text>
              )}
          </Flex>
          <InlineLazyText
            loading={taxesLoading}
            error={taxesError}
            textProps={{ variant: 'heading-3' }}>
            {formatCurrency(todayTotal, orderCurrency)}
          </InlineLazyText>
        </StyledFlex>

        {/* Financing details link */}
        {showFinancing &&
          !hasOnlyAssetsInCart &&
          selectedPaymentType === PaymentType.InFull &&
          storefrontStep !== StorefrontSteps.ORDER_PLACED && (
            <Box marginTop="16px">
              <ExternalLink
                id="asLowAsButton"
                href={getLocalizedSupportUrl(
                  'https://support.electric.ai/electricithub/installment-payments'
                )}
                displayInline={false}
                showIcon={false}>
                {t('sections.orderSummary.asLowAs', {
                  amount: formatCurrency(
                    calculatePreTaxMinMonthlyInstallment(subTotalPrice),
                    orderCurrency
                  ),
                })}
              </ExternalLink>
            </Box>
          )}
      </Box>
    </Pane>
  );
};

export default OrderSummary;
